/**
 * Fechar contas.
 *
 * A nota "o app só organiza — o pagamento acontece fora" não é rodapé: é a
 * descrição honesta do que o produto faz. Prometer confirmação de pagamento
 * seria mentir, e `Marcar como pago` é declaração de quem participa da
 * transferência, não comprovação.
 */
import { formatMoney } from '@nv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useState } from 'react';

import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { Card, ErrorState, Skeleton } from '../../ui/primitives';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { readBalances } from './api';

async function marcarPago(
  tripId: string,
  // A moeda é a da viagem: o servidor a põe, o cliente não manda.
  corpo: { fromMemberId: string; toMemberId: string; amountMinor: number },
) {
  const res = await fetch(`/api/v1/trips/${tripId}/settlements`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      'idempotency-key': crypto.randomUUID(),
    },
    body: JSON.stringify(corpo),
  });
  if (!res.ok) {
    const erro = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(erro.message ?? 'Não foi possível marcar como pago.');
  }
  return res.json();
}

export function FecharScreen({
  tripId,
  currency,
  nomes,
}: {
  tripId: string;
  currency: string;
  nomes: Record<string, string>;
}) {
  const cliente = useQueryClient();
  const { fotoDe } = usePessoas();
  // Transferências marcadas nesta visita: o servidor já não as devolve (o
  // saldo zerou), mas a tela as mantém como "✓ Pago" até sair — é o
  // retorno visual do toque (refino final), não um dado novo.
  const [pagas, setPagas] = useState<{ from: string; to: string; amount: number }[]>([]);

  const dados = useQuery({
    queryKey: ['financeiro', 'saldos', tripId, currency],
    queryFn: () => readBalances(tripId),
  });

  const marcar = useMutation({
    mutationFn: (t: { from: string; to: string; amount: number }) =>
      marcarPago(tripId, {
        fromMemberId: t.from,
        toMemberId: t.to,
        amountMinor: t.amount,
      }),
    onSuccess: (_r, t) => {
      setPagas((atual) => [...atual, t]);
      void cliente.invalidateQueries({ queryKey: ['financeiro'] });
    },
  });

  if (dados.isLoading) {
    return (
      <section className="fin">
        <h1 className="fin__titulo">Fechar contas</h1>
        <Skeleton height={48} count={3} />
      </section>
    );
  }

  if (dados.isError) {
    return (
      <section className="fin">
        <h1 className="fin__titulo">Fechar contas</h1>
        <ErrorState
          title="Não foi possível calcular os saldos"
          description="Nenhum valor foi alterado. Tente de novo em instantes."
          onRetry={() => void dados.refetch()}
        />
      </section>
    );
  }

  const { balances, transfers } = dados.data!;
  const nomeDe = (id: string) => nomes[id] ?? 'Participante';
  const primeiro = (id: string) => nomeDe(id).split(/\s+/)[0];
  const tocados = new Set(pagas.flatMap((t) => [t.from, t.to]));
  const visiveis = balances.filter((b) => b.balanceMinor !== 0 || tocados.has(b.memberId));
  const total = transfers.length + pagas.length;
  const tudoAcertado = transfers.length === 0 && balances.every((b) => b.balanceMinor === 0);
  const avatar = (id: string, tamanho: number) => <FotoAvatar nome={nomeDe(id)} url={fotoDe(id).url} cor={fotoDe(id).cor} tamanho={tamanho} />;

  const cartao = (t: { from: string; to: string; amount: number }, paga: boolean) => (
    <div className={paga ? 'fin__transferencia fin__transferencia--paga' : 'fin__transferencia'} key={`${paga ? 'p' : 'a'}-${t.from}-${t.to}-${t.amount}`}>
      <span className="fin__transferencia-quem" aria-hidden="true">
        {avatar(t.from, 32)}
        <Icon name="avancar" size={14} />
        {avatar(t.to, 32)}
      </span>
      <span className="fin__transferencia-texto">
        <span className="fin__transferencia-valor">{formatMoney(t.amount, currency)}</span>
        <span className="fin__transferencia-quem-texto">
          {primeiro(t.from)} paga {primeiro(t.to)}
        </span>
      </span>
      {paga ? (
        <span className="fin__transferencia-pago">
          <Icon name="concluir" size={14} /> Pago
        </span>
      ) : (
        <button type="button" className="nv-btn nv-btn--chip" disabled={marcar.isPending} onClick={() => marcar.mutate(t)}>
          Marcar como pago
        </button>
      )}
    </div>
  );

  return (
    <section className="fin fin-fechar">
      <header className="fin-nova__header">
        <h1 className="fin__titulo">Fechar contas</h1>
      </header>

      {tudoAcertado ? (
        <div className="fin-fechar__acertado nv-pop" role="status">
          <Ilustracao nome="concluido" tamanho={96} />
          <b>{pagas.length > 0 ? 'Tudo acertado' : 'Todo mundo está quite'}</b>
          <span>
            {pagas.length > 0
              ? pagas.length === 1
                ? 'A transferência foi marcada.'
                : `As ${pagas.length} transferências foram marcadas.`
              : 'Não há nada a acertar.'}
          </span>
        </div>
      ) : (
        <Card variant="surf" lista className="fin-fechar__saldos">
          {visiveis.map((b) => (
            <div className="fin__saldo-linha" key={b.memberId}>
              {avatar(b.memberId, 30)}
              <span className="fin__saldo-nome">{nomeDe(b.memberId)}</span>
              <span className={b.balanceMinor > 0 ? 'fin__valor fin__valor--recebe' : b.balanceMinor < 0 ? 'fin__valor fin__valor--paga' : 'fin__valor fin__valor--quite'}>
                {b.balanceMinor === 0 ? 'quite' : `${b.balanceMinor > 0 ? '+' : '−'}${formatMoney(Math.abs(b.balanceMinor), currency)}`}
              </span>
            </div>
          ))}
        </Card>
      )}

      {total > 0 && (
        <>
          <div className="fin__secao">
            <span className="nv-section-label">
              {total === 1 ? '1 transferência resolve tudo' : `${total} transferências resolvem tudo`}
            </span>
            <span className="fin-fechar__conta">
              {pagas.length}/{total}
            </span>
          </div>
          <div className="fin-fechar__lista">
            {transfers.map((t) => cartao(t, false))}
            {pagas.map((t) => cartao(t, true))}
          </div>
        </>
      )}

      <p className="fin-fechar__nota">
        O app só organiza — o pagamento acontece fora dele. “Marcar como pago”
        registra que vocês combinaram, não confirma que o dinheiro chegou.
      </p>

      {marcar.isError && (
        <p className="auth-error" role="alert">
          {(marcar.error as Error).message}
        </p>
      )}
    </section>
  );
}
